// src/components/SymptomSelector/SymptomSelector.jsx
// Multi-step symptom selection flow that leads to a targeted chat session
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TherapistActionModal from '../TherapistActionModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STEPS = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  SEVERITY: 'severity',
};

const SymptomSelector = ({ token }) => {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.PRIMARY);
  const [primarySymptoms, setPrimarySymptoms] = useState([]);
  const [secondaryOptions, setSecondaryOptions] = useState([]);
  const [suggestions, setSuggestions] = useState(null);

  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [selectedSecondary, setSelectedSecondary] = useState(null);
  const [selectedTertiary, setSelectedTertiary] = useState(null);
  const [symptomPath, setSymptomPath] = useState([]);
  const [severityScore, setSeverityScore] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Load primary symptoms on mount
  useEffect(() => {
    loadPrimarySymptoms();
  }, []);

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  });

  const loadPrimarySymptoms = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/api/v1/symptoms/primary`, { headers: headers() });
      setPrimarySymptoms(res.data.symptoms || []);
    } catch (err) {
      console.error('Kunde inte hämta symptom:', err);
      setError('Kunde inte ladda alternativ. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPrimary = async (symptom) => {
    setSelectedPrimary(symptom);
    setSelectedSecondary(null);
    setSelectedTertiary(null);
    setSymptomPath([symptom.id]);
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/api/v1/symptoms/${symptom.id}/secondary`, {
        headers: headers(),
      });
      setSecondaryOptions(res.data.options || []);
      setStep(STEPS.SECONDARY);
    } catch (err) {
      console.error('Kunde inte hämta sekundära alternativ:', err);
      setError('Kunde inte ladda alternativ. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSecondary = async (option) => {
    setSelectedSecondary(option);
    setSelectedTertiary(null);
    const newPath = [selectedPrimary.id, option.id];
    setSymptomPath(newPath);

    // Load tertiary + suggestions silently in the background (no visible step)
    let finalPath = newPath;
    if (option.area) {
      try {
        const res = await axios.get(`${API_BASE}/api/v1/symptoms/${option.area}/tertiary`, {
          headers: headers(),
        });
        const options = res.data.options || [];
        // Keep the first tertiary as implicit selection if available
        if (options.length > 0) {
          setSelectedTertiary(options[0]);
          finalPath = [selectedPrimary.id, option.id, options[0].id];
          setSymptomPath(finalPath);
        }
      } catch (err) {
        console.warn('Inga tertiära alternativ:', err);
      }
    }

    // Load suggestions silently
    try {
      const res = await axios.post(
        `${API_BASE}/api/v1/symptoms/suggestions`,
        { symptom_path: finalPath },
        { headers: headers() }
      );
      setSuggestions(res.data);
    } catch (err) {
      console.warn('Kunde inte hämta förslag:', err);
    }

    setSeverityScore(null);
    setStep(STEPS.SEVERITY);
  };

  const handleStartChat = () => {
    navigate('/client/chat', {
      state: {
        symptomContext: {
          contextId: 'symptom',
          symptomPath,
          selectedPrimary,
          selectedSecondary,
          selectedTertiary,
          suggestions,
          severityScore,
        },
      },
    });
  };

  const handleBack = () => {
    if (step === STEPS.SECONDARY) {
      setStep(STEPS.PRIMARY);
    } else if (step === STEPS.SEVERITY) {
      setStep(STEPS.SECONDARY);
    }
  };

  const stepLabels = {
    [STEPS.PRIMARY]: 'Vad känner du av?',
    [STEPS.SECONDARY]: 'Vad handlar det om?',
    [STEPS.SEVERITY]: 'Hur allvarligt känns det?',
  };

  const stepNumbers = {
    [STEPS.PRIMARY]: 1,
    [STEPS.SECONDARY]: 2,
    [STEPS.SEVERITY]: 3,
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-primary">Starta chat</h1>
              <p className="text-sm text-secondary mt-0.5">{stepLabels[step]}</p>
            </div>
            <button
              onClick={() => navigate('/client')}
              className="text-sm text-tertiary hover:text-primary transition-colors"
            >
              Avbryt
            </button>
          </div>

          {/* Progress bar */}
          <div className="flex gap-1.5 mt-4">
            {Object.values(STEPS).map((s, idx) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  stepNumbers[step] > idx
                    ? 'bg-brand-primary-600'
                    : stepNumbers[step] === idx + 1
                    ? 'bg-brand-primary-400'
                    : 'bg-neutral-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Error */}
        {error && (
          <div className="bg-error-50 border border-error-400 rounded-lg p-4 mb-4 text-error-700 text-sm">
            {error}
            <button
              onClick={() => {
                setError(null);
                if (step === STEPS.PRIMARY) loadPrimarySymptoms();
              }}
              className="ml-2 underline"
            >
              Försök igen
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-brand-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Step 1: Primary symptoms */}
        {!loading && step === STEPS.PRIMARY && (
          <div>
            <p className="text-sm text-secondary mb-4">
              Välj det som bäst beskriver hur du känner dig just nu.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {primarySymptoms.map((symptom) => (
                <button
                  key={symptom.id}
                  onClick={() => handleSelectPrimary(symptom)}
                  className="flex flex-col items-start gap-2 p-4 bg-white border-2 border-neutral-200 rounded-xl hover:border-brand-primary-400 hover:shadow-sm transition-all text-left"
                >
                  <span className="text-2xl">{symptom.emoji}</span>
                  <div>
                    <p className="font-medium text-sm text-gray-900">{symptom.name || symptom.label}</p>
                    {symptom.description && (
                      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{symptom.description}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Secondary options */}
        {!loading && step === STEPS.SECONDARY && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">{selectedPrimary?.emoji}</span>
              <p className="text-base font-medium text-gray-900">{selectedPrimary?.name || selectedPrimary?.label}</p>
            </div>
            <p className="text-sm text-secondary mb-4">
              Vad handlar det om för dig?
            </p>
            <div className="flex flex-col gap-2">
              {secondaryOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleSelectSecondary(option)}
                  className="flex items-center gap-3 p-4 bg-white border-2 border-neutral-200 rounded-xl hover:border-brand-primary-400 hover:shadow-sm transition-all text-left"
                >
                  <div className="w-2 h-2 rounded-full bg-brand-primary-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-800">{option.name || option.label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={handleBack}
              className="mt-4 text-sm text-tertiary hover:text-primary transition-colors"
            >
              ← Tillbaka
            </button>
          </div>
        )}

        {/* Step 3: Severity scale */}
        {!loading && step === STEPS.SEVERITY && (
          <div>
            <p className="text-sm text-secondary mb-2">
              Hur jobbigt känns det <span className="font-medium text-primary">just nu</span>?
            </p>
            <p className="text-xs text-tertiary mb-6">
              Det hjälper AI:n att anpassa stödet från start.
            </p>

            {/* Scale buttons */}
            <div className="grid grid-cols-5 gap-2 mb-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                const isSelected = severityScore === n;
                const color =
                  n <= 3
                    ? isSelected
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 hover:border-green-400'
                    : n <= 6
                    ? isSelected
                      ? 'bg-yellow-400 text-white border-yellow-400'
                      : 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100 hover:border-yellow-400'
                    : n <= 8
                    ? isSelected
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 hover:border-orange-400'
                    : isSelected
                    ? 'bg-red-500 text-white border-red-500'
                    : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:border-red-400';
                return (
                  <button
                    key={n}
                    onClick={() => setSeverityScore(n)}
                    className={`flex flex-col items-center justify-center py-3 rounded-xl border-2 font-bold transition-all duration-150 ${color} ${
                      isSelected ? 'scale-105 shadow-md' : ''
                    }`}
                  >
                    <span className="text-lg leading-none">{n}</span>
                    {n === 1 && <span className="text-[9px] font-normal leading-tight mt-0.5 text-center">Inte jobbigt alls</span>}
                    {n === 10 && <span className="text-[9px] font-normal leading-tight mt-0.5 text-center">Akut kris</span>}
                  </button>
                );
              })}
            </div>

            {/* Booking prompt for high severity */}
            {severityScore >= 9 && (
              <div className="mb-4 p-4 bg-brand-primary-600 rounded-xl">
                <p className="text-sm font-semibold text-white mb-1">Det verkar som att du har det riktigt svårt just nu.</p>
                <p className="text-sm text-white/80 mb-3">Vill du boka ett samtal med en terapeut?</p>
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="w-full py-2.5 bg-white text-brand-primary-600 font-semibold rounded-lg hover:bg-white/90 transition-colors text-sm"
                >
                  Boka samtal hos terapeut
                </button>
              </div>
            )}

            {/* Selected score description */}
            {severityScore !== null && severityScore < 9 && (
              <div className={`rounded-xl p-4 mb-6 text-sm font-medium text-center ${
                severityScore <= 3
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : severityScore <= 6
                  ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                  : severityScore <= 8
                  ? 'bg-orange-50 text-orange-800 border border-orange-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {severityScore <= 3 && 'Du verkar ha utrymme att utforska detta lugnt.'}
                {severityScore >= 4 && severityScore <= 6 && 'Det märks att det här tar på krafterna. Vi tar det i din takt.'}
                {severityScore >= 7 && severityScore <= 8 && 'Det låter som att det är riktigt tungt just nu. AI:n kommer börja med att ge dig stöd.'}
                {severityScore >= 9 && 'Tack för att du delar det. Vi prioriterar att du mår bättre nu.'}
              </div>
            )}

            <button
              onClick={handleStartChat}
              disabled={severityScore === null}
              className="w-full py-4 bg-brand-primary-600 text-white font-semibold rounded-xl hover:bg-brand-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-base shadow-sm"
            >
              Starta chat →
            </button>

            <button
              onClick={handleBack}
              className="mt-3 w-full py-3 text-sm text-tertiary hover:text-primary transition-colors"
            >
              ← Tillbaka
            </button>
          </div>
        )}
      </div>

      <TherapistActionModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
      />
    </div>
  );
};

export default SymptomSelector;
